/* eslint-disable no-console */
import { useCallback, useEffect, useRef, useState } from 'react';
import { cecClosed, updateTile, fetchEngagements } from '../utils/request';
import { ServiceInstanceEvent } from '@jarvis/jweb-core';
import {
  sendShowHideCECEvent,
  addCECShownListener,
  addLoginChangedListener,
  addPrinterSelectedListener,
  addServiceInstanceClosedListener,
  addUnloadCustomListener,
  getUserAuthToken,
  sendShowHpxBannerEvent
} from 'jweb';
import { addIndexAsProperty, debounce, isNil, sortTilesByPriority } from '../utils/functional';
import {
  setSession,
  getPrinterInfo,
  setJwt,
  setPrinterInfo,
  getCurrentJwt,
  getSession,
  getInitialPrinterDataFetched,
  setInitialPrinterDataFetched
} from '../utils/persistData';
import { injectUDLScript, injectUDLTags, trackAppEventListenerUDLEvent } from '../utils/analytics';
import {
  trackAppEventListenerSimpleUIEvent,
  sendShowCECSimpleUIEvent
} from '../utils/simpleUIAnalytics';
import { RTL_LOCALES } from '../utils/constants';
import { useRefState } from './utility';

const createHandleTileInteraction = (currentTiles, tileSetterFn) => (tile, interactiontype) => {
  const engagementNames = Array.isArray(tile)
    ? tile.map(t => t.engagementName)
    : [tile.engagementName];

  const updateEngagementList = () => {
    tileSetterFn(
      addIndexAsProperty(
        currentTiles
          .map(t => {
            if (engagementNames.includes(t.engagementName)) {
              return {
                ...t,
                [interactiontype]: true
              };
            }
            return t;
          })
          .filter(t => !t.dismissed)
      )
    );
  };

  if (interactiontype === 'dismissed' || interactiontype === 'clicked') {
    return updateTile(engagementNames[0], { [interactiontype]: true }).then(
      () => {
        updateEngagementList();
        return Promise.resolve();
      },
      () => {
        return Promise.resolve();
      }
    );
  } else {
    updateEngagementList();
    engagementNames.forEach(name => {
      updateTile(name, { [interactiontype]: true });
    });
  }
};

export const useInitApplication = (initialData, isFullScreen) => {
  const [tiles, tilesRef, setTiles] = useRefState([]);
  const [fetching, setFetching] = useState(false);
  const [isUpgradeToHpxFlagAvailable, setIsUpgradeToHpxFlagAvailable] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isAppShown, setIsAppShown] = useState(false);
  const [theme, setTheme] = useState({ fullScreen: isFullScreen, dir: 'ltr' });

  const listenersList = useRef([]);

  const handleTileInteraction = useCallback(
    (tile, interactiontype) => createHandleTileInteraction(tiles, setTiles)(tile, interactiontype),
    [tiles]
  );

  const setTileCompletedState = tileName => {
    sessionStorage.removeItem('clickedTile');
    const tile = tiles.filter(t => t.engagementName === tileName);
    handleTileInteraction(tile, 'clicked');
  };

  const handleNativeStateChange = useCallback(
    debounce(function () {
      console.log(`calling fetch tiles`);
      const updateTiles = newTiles => {
        const updatedTileList = newTiles.map(newTile =>
          tilesRef.current.reduce((acc, existingTile) => {
            return JSON.stringify(acc) === JSON.stringify(existingTile) ? existingTile : acc;
          }, newTile)
        );
        setTiles(addIndexAsProperty(updatedTileList));
      };
      fetchEngagements({
        sessionId: getSession(),
        ruleType: 'dataDriven'
      }).then(({ engagements: newTiles, isUpgradeToHpxTileAvailable }) => {
        console.log(
          `fetched dataDriven tiles: ${JSON.stringify(newTiles)}`,
          isUpgradeToHpxTileAvailable
        );
        updateTiles(newTiles);
        setIsUpgradeToHpxFlagAvailable(isUpgradeToHpxTileAvailable);
        setIsInitialLoad(false);
        if (isUpgradeToHpxTileAvailable) {
          console.log('sending the showHpxBannerEvent');
          sendShowHpxBannerEvent();
        }
      });
    }, 500),
    []
  );

  const disableListeners = useCallback(() => {
    listenersList.current.forEach(listener => {
      if ((listener.disableInParentCec || isFullScreen) && listener.removeListener) {
        listener.removeListener();
        listener.islistening = false;
      }
    });
  }, []);

  const attachListener = async (listenerFunc, listenerCallback, name, isDisableInMainCEC) => {
    const removeListener = await listenerFunc(listenerCallback);
    listenersList.current.push({
      name: name,
      removeListener: removeListener,
      islistening: true,
      callback: listenerCallback,
      disableInParentCec: isDisableInMainCEC
    });
    return removeListener;
  };

  useEffect(() => {
    if (initialData) {
      const locale = initialData.locale?.toLowerCase();
      setTiles(addIndexAsProperty(initialData.engagements));
      injectUDLTags(locale);
      injectUDLScript();
      setSession(initialData.sessionId);

      if (initialData.widgetText && initialData.widgetText.title) {
        document.title = initialData.widgetText.title;
      }

      const textDirection = () => {
        const lang = locale?.split(/-|_/)[0];
        return RTL_LOCALES.includes(lang) ? 'rtl' : 'ltr';
      };
      setTheme({ fullScreen: isFullScreen, dir: textDirection() });
    }
  }, [initialData]);

  useEffect(() => {
    // e.g. logged in user logs out or vice versa
    // refetch all tiles with/without JWT token
    const onLoginChange = (token = '') => {
      console.log(`received login change ${!!token}`);
      if (getCurrentJwt() !== token) {
        setJwt(token);
        handleNativeStateChange();
        trackAppEventListenerUDLEvent('loginChanged', !!token);
        trackAppEventListenerSimpleUIEvent('LoginChanged');
      }
    };

    let removeListenerCallback;
    if (initialData) {
      removeListenerCallback = attachListener(
        addLoginChangedListener,
        onLoginChange,
        'loginChanged',
        true
      );
    }

    return removeListenerCallback;
  }, [initialData]);

  useEffect(() => {
    // Check session storage for any printer selected events triggered before CEC load.
    // the events are placed in storage by bootloader.
    const onPrinterChange = newPrinterInfo => {
      console.log('received new printer change', JSON.stringify(newPrinterInfo));
      if (isNil(newPrinterInfo.PrinterUUID)) {
        newPrinterInfo.PrinterUUID = '';
      }
      if (isNil(newPrinterInfo.PrinterProductNumber)) {
        newPrinterInfo.PrinterProductNumber = '';
      }
      if (isNil(newPrinterInfo.SessionId)) {
        newPrinterInfo.SessionId = '';
      }
      const currentPrinterInfo = getPrinterInfo();
      const isInitialDataFetched = getInitialPrinterDataFetched();

      if (
        newPrinterInfo.PrinterUUID !== currentPrinterInfo.device_uuid ||
        newPrinterInfo.PrinterProductNumber !== currentPrinterInfo.product_number ||
        newPrinterInfo.SessionId !== currentPrinterInfo.printer_sessionId
      ) {
        setPrinterInfo(newPrinterInfo);
        handleNativeStateChange();
        trackAppEventListenerUDLEvent('printerChanged');
        trackAppEventListenerSimpleUIEvent('PrinterChanged');
        console.log(
          'check hpx banner event flag',
          isUpgradeToHpxFlagAvailable,
          JSON.stringify(currentPrinterInfo)
        );
      } else if (isInitialDataFetched) {
        setInitialPrinterDataFetched(false);
        handleNativeStateChange();
      }
    };

    let removeListenerCallback;
    if (initialData || tiles?.length) {
      removeListenerCallback = attachListener(
        addPrinterSelectedListener,
        onPrinterChange,
        'printerSelected'
      );
    }
    return removeListenerCallback;
  }, [tiles.length, initialData]);

  useEffect(() => {
    // e.g. user has finished engagement workflow
    // refetch all tiles
    const onCecShown = async () => {
      console.log(`received CEC shown`);
      const token = await getUserAuthToken(false);
      setJwt(token);
      if (sessionStorage.getItem('clickedTile')) {
        setTileCompletedState(sessionStorage.getItem('clickedTile'));
      }
      handleNativeStateChange();
    };

    let removeListenerCallback;
    if (initialData) {
      setIsUpgradeToHpxFlagAvailable(initialData?.isUpgradeToHpxTileAvailable);
      removeListenerCallback = attachListener(addCECShownListener, onCecShown, 'cecShown');
    }
    return removeListenerCallback;
  }, [initialData]);

  useEffect(() => {
    // e.g. Catches different states of the Service Routing Plugin -> Whether interstials are running, launching, failed to launch, and closed.
    const onServiceInstanceClosed = async args => {
      console.log(`received ServiceInstanceStateChange closed`);
      if (sessionStorage.getItem('clickedTile')) {
        setTileCompletedState(sessionStorage.getItem('clickedTile'));
      }
      // If logged in from showall screen, then fetch token before reloading main CEC
      if (sessionStorage.getItem('openedShowAll')) {
        const token = await getUserAuthToken(false);
        setJwt(token);
        sessionStorage.removeItem('openedShowAll');
      }
      // re-enabiling listeners on cec shown.
      // currently its only login change listener
      // todo: utility with switch case, to enable corresponding listeners bases on its name.
      if (!isFullScreen) {
        listenersList.current.forEach(listener => {
          const attachListener = async listener => {
            if (!listener.islistening) {
              listener.removeListener = await addLoginChangedListener(listener.callback);
              listener.islistening = true;
            }
          };
          attachListener(listener);
        });
      }
      handleNativeStateChange();
    };

    let removeListenerCallback;
    if (initialData) {
      removeListenerCallback = attachListener(
        addServiceInstanceClosedListener,
        onServiceInstanceClosed,
        ServiceInstanceEvent.jarvisEventServiceInstanceClosed
      );
    }
    return removeListenerCallback;
  }, [initialData]);

  useEffect(() => {
    return addUnloadCustomListener(handleNativeStateChange);
  }, []);

  useEffect(() => {
    if (initialData) {
      if (!tiles.length && isAppShown) {
        console.log(`sending hide CEC event`);
        sendShowHideCECEvent(false); // tell the app to hide us
        setIsAppShown(false);
        cecClosed();
      } else if (tiles.length && !isAppShown) {
        console.log(`sending show CEC event`, isUpgradeToHpxFlagAvailable, initialData);
        sendShowHideCECEvent(true);
        sendShowCECSimpleUIEvent();
        setIsAppShown(true);
        if (isUpgradeToHpxFlagAvailable) {
          console.log('sending the showHpxBannerEvent');
          sendShowHpxBannerEvent();
        }
      }
    }
  }, [tiles.length, initialData, isUpgradeToHpxFlagAvailable]);

  const prioritizedTiles = sortTilesByPriority(tiles);
  return {
    tiles: prioritizedTiles,
    handleNativeStateChange,
    handleTileInteraction,
    isUpgradeToHpxFlagAvailable,
    disableListeners,
    fetching,
    isInitialLoad,
    theme
  };
};
